import SSEEvents from '@src/shared/services/sse-events';
import authEvents from '@shared/services/auth-events';
import keycloak from '@src/keycloak';
import MCPMarketEnvironmentDetection from '@src/shared/utils/market-environment-detection';

class SSEConsumer {
  private static instance: SSEConsumer | null = null;

  private source: EventSource | null;
  private onMessage: any;
  private onError: any;
  private sourceURL: string | URL;
  private timer: any;
  private token: string | undefined;
  private userId: string | undefined;

  private constructor(
    sourceURL: string,
    token: string | undefined,
    userId: string | undefined,
    onMessage?: () => any,
    onError?: (error: Event) => any
  ) {
    this.source = null;
    this.onMessage = onMessage;
    this.sourceURL = sourceURL || '';
    this.onError = onError;
    this.timer = null;
    this.token = token;
    this.userId = userId;
    try {
      this.initialize();
    } catch (error: any) {
      console.error(`Failed to initialize EventSource with message: ${error.message}`);
      this.source = null;
    }
  }

  public static getInstance(
    sourceURL: string,
    token: string | undefined,
    userId: string | undefined,
    onMessage?: () => any,
    onError?: (error: Event) => any
  ): SSEConsumer {
    if (!SSEConsumer.instance) {
      SSEConsumer.instance = new SSEConsumer(sourceURL, token, userId, onMessage, onError);
    }
    return SSEConsumer.instance;
  }

  public static destroyInstance(): void {
    if (SSEConsumer.instance) {
      SSEConsumer.instance.disconnect();
      SSEConsumer.instance = null;
    }
  }

  public getConnectionURL() {
    const { countryCode } = MCPMarketEnvironmentDetection.getMarketEnvironment();
    return `${this.sourceURL}?token=${this.token}&id=${this.userId}&country=${countryCode}`;
  }

  public async freshReload() {
    if (!keycloak.authenticated) {
      this.disconnect();
      return;
    }
    await keycloak.updateToken(-1);
    this.token = keycloak.token;
    this.initialize();
  }

  private initialize() {
    clearTimeout(this.timer);
    this.source = new EventSource(this.getConnectionURL());
    this.source.onmessage = this.onMessage || (() => {});
    this.source.onopen = () => {
      SSEEvents.emitSSEOpen();
    };
    this.source.onerror = async (error) => {
      if (error) {
        SSEEvents.emitSSETokenError(this, error);
      }
      this.onError && this.onError(error);

      if (this.source?.readyState === 2) {
        if (!keycloak.authenticated) {
          this.disconnect();
          return;
        }
        this.timer = setTimeout(async () => {
          this.freshReload();
        }, 3000);
      }
    };
    authEvents.subscribeToLogoutKeycloakUser(() => {
      this.disconnect();
    });
  }

  public subscribeToEvent = (eventName: string, callback: (data: any) => any) => {
    this.source?.addEventListener(eventName, (event) => {
      try {
        const data = JSON.parse((event as MessageEvent).data);
        callback(data);
      } catch (error: any) {
        console.error(`Failed to parse data from MessageEvent with message: ${error.message}`);
      }
    });
  };

  public disconnect = () => {
    clearTimeout(this.timer);
    this.source?.close();
  };
}

export default SSEConsumer;
